import { booleanLookup } from '../../../common/constants/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICmdbCiWinClusterNode } from '../../../services/serviceNow/cmdbCiWinClusterNode/cmdbCiWinClusterNode.model';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { RootState } from '../../app.model';
import {
  deleteCmdbCiWinClusterNode,
  getCmdbCiWinClusterNodeById,
  searchCmdbCiWinClusterNode,
  saveCmdbCiWinClusterNode,
  processDataCmdbCiWinClusterNode,
} from './cmdbCiWinClusterNode.action';
import { ICmdbCiWinClusterNodeState } from './cmdbCiWinClusterNode.model';

export const initialState: ICmdbCiWinClusterNodeState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  processData: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const CmdbCiWinClusterNodeSlice = createSlice({
  name: 'CmdbCiWinClusterNode',
  initialState,
  reducers: {
    clearCmdbCiWinClusterNode: () => {
      return initialState;
    },
    clearCmdbCiWinClusterNodeMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
      state.processData.messages = [];
    },
    clearCmdbCiWinClusterNodeGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchCmdbCiWinClusterNode.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchCmdbCiWinClusterNode.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<ICmdbCiWinClusterNode>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchCmdbCiWinClusterNode.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getCmdbCiWinClusterNodeById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getCmdbCiWinClusterNodeById.fulfilled.type]: (state, action: PayloadAction<ICmdbCiWinClusterNode>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getCmdbCiWinClusterNodeById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveCmdbCiWinClusterNode.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveCmdbCiWinClusterNode.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveCmdbCiWinClusterNode.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteCmdbCiWinClusterNode.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteCmdbCiWinClusterNode.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteCmdbCiWinClusterNode.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },

    // Process Data
    [processDataCmdbCiWinClusterNode.pending.type]: (state) => {
      state.processData.loading = true;
      state.processData.messages = [];
    },
    [processDataCmdbCiWinClusterNode.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.processData.loading = false;
      state.processData.hasErrors = false;
      state.processData.messages = action.payload.messages;
    },
    [processDataCmdbCiWinClusterNode.rejected.type]: (state) => {
      state.processData.loading = false;
      state.processData.hasErrors = true;
    },
  },
});

// A selector
export const CmdbCiWinClusterNodeSelector = (state: RootState) => state.cmdbCiWinClusterNode;

// Actions
export const {
  clearCmdbCiWinClusterNode,
  clearCmdbCiWinClusterNodeMessages,
  clearCmdbCiWinClusterNodeGetById,
  setTableColumnSelection,
} = CmdbCiWinClusterNodeSlice.actions;

// The reducer
export default CmdbCiWinClusterNodeSlice.reducer;
